import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../lib/apiClient';
import { usePortalPatientData } from './portalContext';
import { EmptyState, ErrorBanner, Panel, PortalPage, formatPortalDateTime, statusPillClass } from './ui';

type PortalDocumentType = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  fields?: Array<{ id: string; label: string; type: string; required?: boolean }>;
};

type PortalDocument = {
  id: string;
  fileName: string;
  title?: string | null;
  description?: string | null;
  category?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  createdAt: string;
  reviewStatus: string;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
  metadata?: Record<string, unknown>;
  downloadUrl: string;
  documentType?: PortalDocumentType | null;
};

type PortalDocumentsResponse = {
  data?: PortalDocument[];
  meta?: {
    limit: number;
    offset: number;
    count: number;
  };
};

type PortalDocumentTypesResponse = {
  data?: PortalDocumentType[];
};

type PortalPresignResponse = {
  data?: {
    key: string;
    uploadUrl: string;
    url: string;
    contentType: string;
    sizeBytes: number;
  };
};

type PortalDocumentCreateResponse = {
  data?: PortalDocument;
};

function formatSize(value?: number | null) {
  const bytes = Number(value ?? 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MedicalRecordsPage() {
  const { t } = useTranslation();
  const { fetchJson } = useApiClient();
  const { buildPortalPath, refreshNonce } = usePortalPatientData();
  const [rows, setRows] = useState<PortalDocument[]>([]);
  const [documentTypes, setDocumentTypes] = useState<PortalDocumentType[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);

  const selectedType = useMemo(
    () => documentTypes.find((item) => item.id === selectedTypeId) || null,
    [documentTypes, selectedTypeId],
  );

  const loadPageData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Load each section independently so one failing endpoint can't blank the page.
      const [documentsResult, typeResult] = await Promise.allSettled([
        fetchJson<PortalDocumentsResponse>(buildPortalPath('/portal/documents?limit=20&offset=0')),
        fetchJson<PortalDocumentTypesResponse>(buildPortalPath('/portal/document-types')),
      ]);
      const documentsResponse = documentsResult.status === 'fulfilled' ? documentsResult.value : null;
      const typeResponse = typeResult.status === 'fulfilled' ? typeResult.value : null;
      setRows(Array.isArray(documentsResponse?.data) ? documentsResponse.data : []);
      const nextTypes = Array.isArray(typeResponse?.data) ? typeResponse.data : [];
      setDocumentTypes(nextTypes);
      setSelectedTypeId((current) => current || nextTypes[0]?.id || '');
      if (documentsResult.status === 'rejected' && typeResult.status === 'rejected') {
        setError(documentsResult.reason?.message || t('records.loadError'));
      }
    } catch (err: any) {
      setError(err?.message || t('records.loadError'));
    } finally {
      setLoading(false);
    }
  }, [buildPortalPath, fetchJson, t]);

  useEffect(() => {
    void loadPageData();
  }, [loadPageData, refreshNonce]);

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setUploadError(null);
    setUploadMessage(null);
    if (file && !title) {
      const baseName = file.name.replace(/\.[^.]+$/, '');
      setTitle(baseName);
    }
  }

  async function handleUpload() {
    if (!selectedFile || !selectedTypeId) {
      setUploadError(t('records.selectTypeAndFile'));
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      setUploadMessage(null);

      const presignResponse = await fetchJson<PortalPresignResponse>(buildPortalPath('/portal/documents/presign'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: selectedFile.name,
          contentType: selectedFile.type || 'application/octet-stream',
          sizeBytes: selectedFile.size,
        }),
      });

      const presign = presignResponse?.data;
      if (!presign?.uploadUrl || !presign?.key || !presign?.url) {
        throw new Error('Upload presign response was incomplete.');
      }

      const uploadResult = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': selectedFile.type || presign.contentType || 'application/octet-stream',
        },
        body: selectedFile,
      });
      if (!uploadResult.ok) {
        throw new Error(`File upload failed ${uploadResult.status}`);
      }

      await fetchJson<PortalDocumentCreateResponse>(buildPortalPath('/portal/documents'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: selectedFile.name,
          title: title.trim() || undefined,
          description: description.trim() || undefined,
          mimeType: selectedFile.type || presign.contentType,
          storageKey: presign.key,
          url: presign.url,
          sizeBytes: selectedFile.size,
          documentTypeId: selectedTypeId,
        }),
      });

      setUploadMessage(t('records.uploadSuccess'));
      setSelectedFile(null);
      setTitle('');
      setDescription('');
      const input = document.getElementById('patient-document-upload') as HTMLInputElement | null;
      if (input) input.value = '';
      await loadPageData();
    } catch (err: any) {
      setUploadError(err?.message || t('records.uploadError'));
    } finally {
      setUploading(false);
    }
  }

  return (
    <PortalPage title={t('records.title')} description={t('records.description')}>
      {error ? <ErrorBanner message={error} /> : null}
      {uploadError ? <ErrorBanner message={uploadError} /> : null}
      {uploadMessage ? <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">{uploadMessage}</div> : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
        <Panel title={t('records.uploadDocument')}>
          {!loading && documentTypes.length === 0 ? (
            <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {t('records.noDocumentTypes')}
            </div>
          ) : null}
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">{t('records.documentType')}</label>
              <select
                value={selectedTypeId}
                onChange={(event) => setSelectedTypeId(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              >
                {documentTypes.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {selectedType?.description ? <p className="mt-2 text-xs text-slate-500">{selectedType.description}</p> : null}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">{t('records.file')}</label>
              <input
                id="patient-document-upload"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.heic,.heif,application/pdf,image/png,image/jpeg,image/heic,image/heif"
                onChange={handleFileChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              />
              <p className="mt-2 text-xs text-slate-500">{t('records.fileHint')}</p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">{t('records.titleLabel')}</label>
              <input
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder={t('records.titlePlaceholder')}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">{t('records.descriptionLabel')}</label>
              <textarea
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={t('records.descriptionPlaceholder')}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              />
            </div>

            {selectedFile ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <div className="font-medium text-slate-800">{selectedFile.name}</div>
                <div className="mt-1">{t('records.sizeLabel', { value: formatSize(selectedFile.size) })}</div>
                <div>{t('records.mimeLabel', { value: selectedFile.type || t('records.unknown') })}</div>
              </div>
            ) : null}

            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !selectedTypeId}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {uploading ? t('records.uploading') : t('records.uploadDocument')}
            </button>
          </div>
        </Panel>

        <Panel title={t('records.library')}>
          {loading ? <div className="text-sm text-slate-500">{t('records.loadingDocuments')}</div> : null}
          {!loading && rows.length === 0 ? <EmptyState message={t('records.libraryEmpty')} /> : null}
          <div className="space-y-3">
            {rows.map((documentItem) => (
              <div key={documentItem.id} className="rounded-xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <a
                      href={documentItem.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate text-base font-semibold text-blue-600 hover:underline"
                    >
                      {documentItem.title || documentItem.fileName}
                    </a>
                    <div className="mt-1 text-xs uppercase tracking-wide text-slate-500">
                      {documentItem.documentType?.name || documentItem.category || documentItem.mimeType || t('common.document')}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`pill ${statusPillClass(documentItem.reviewStatus)}`}>{documentItem.reviewStatus}</span>
                    {documentItem.documentType?.code ? <span className="pill pill-info">{documentItem.documentType.code}</span> : null}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-slate-600 md:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <span className="font-medium text-slate-700">{t('records.uploaded')}</span> {formatPortalDateTime(documentItem.createdAt)}
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">{t('records.reviewed')}</span> {formatPortalDateTime(documentItem.reviewedAt)}
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">{t('records.fileSize')}</span> {formatSize(documentItem.sizeBytes)}
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">{t('records.source')}</span> {String(documentItem.metadata?.uploadSource || 'portal')}
                  </div>
                </div>

                {documentItem.description ? <p className="mt-3 text-sm text-slate-600">{documentItem.description}</p> : null}
                {documentItem.rejectionReason ? (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <span className="font-medium">{t('records.reviewNote')}</span> {documentItem.rejectionReason}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </PortalPage>
  );
}
